import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RankBadge } from "@/lib/ranks";
import { PlatformIcons } from "@/lib/platforms";

const GAMES = ["CALL OF DUTY: WARZONE", "CALL OF DUTY: MW4 MULTIPLAYER"];
const GAME_SHORT = { "CALL OF DUTY: WARZONE": "Warzone", "CALL OF DUTY: MW4 MULTIPLAYER": "MW4 MP" };

export default function Teams() {
    const { user, ready, refreshInvites } = useAuth();
    const [tab, setTab] = useState("squads"); // "squads" | "players" | "lft"
    const [captained, setCaptained] = useState([]);
    const [joined, setJoined] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteFor, setInviteFor] = useState(null); // team object
    const [showCreate, setShowCreate] = useState(false);
    const [allPlayers, setAllPlayers] = useState([]);
    const [playersLoading, setPlayersLoading] = useState(false);
    const [lftPlayers, setLftPlayers] = useState([]);
    const [lftLoading, setLftLoading] = useState(false);

    const load = async () => {
        try {
            const [tRes, iRes] = await Promise.all([
                api.get("/my/teams"),
                api.get("/my/invites"),
            ]);
            setCaptained(tRes.data.captained || []);
            setJoined(tRes.data.joined || []);
            setInvites(iRes.data || []);
            refreshInvites?.();
        } catch {} finally { setLoading(false); }
    };
    useEffect(() => { if (user) load(); else setLoading(false); }, [user]);

    // Fetch full player directory when the Players tab is opened (or on first load)
    useEffect(() => {
        if (!user || tab !== "players") return;
        setPlayersLoading(true);
        api.get("/leaderboard/players", { params: { limit: 500, state: "ALL" } })
            .then(r => setAllPlayers(r.data || []))
            .catch(() => setAllPlayers([]))
            .finally(() => setPlayersLoading(false));
    }, [user, tab]);

    // Fetch "Looking for Team" list when the LFT tab is opened
    const loadLft = () => {
        setLftLoading(true);
        api.get("/players/looking-for-team")
            .then(r => setLftPlayers(r.data || []))
            .catch(() => setLftPlayers([]))
            .finally(() => setLftLoading(false));
    };
    useEffect(() => { if (user && tab === "lft") loadLft(); }, [user, tab]);

    if (!ready || loading) return <div className="pt-40 text-center text-white/40 font-mono text-xs tracking-[0.3em]">LOADING…</div>;

    if (!user) return (
        <div className="pt-40 pb-40 text-center" data-testid="teams-signed-out">
            <div className="overline text-white/40 mb-4">[ Sign in required ]</div>
            <h1 className="font-display font-black text-5xl md:text-7xl">Manage your <span className="text-volt">squads</span>.</h1>
            <p className="mt-6 text-white/60">Sign in to see your rosters, invites, and create new teams.</p>
            <Link to="/login" className="btn-primary mt-8 inline-block" data-testid="teams-signin-cta">Sign in →</Link>
        </div>
    );

    return (
        <div className="pt-[68px]" data-testid="teams-page">
            {/* HERO */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 pb-10">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <div className="overline mb-4">[ Roster HQ · {user.gamer_tag} ]</div>
                        <h1 className="font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl">
                            My <span className="text-volt">squads.</span>
                        </h1>
                    </div>
                    <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="open-create-team">+ Create team</button>
                </div>
            </section>

            {/* TAB SWITCHER */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 border-b border-white/10">
                <div className="flex items-center gap-8" data-testid="teams-tabs">
                    <button
                        onClick={() => setTab("squads")}
                        data-testid="teams-tab-squads"
                        className={`relative font-mono text-xs tracking-[0.24em] uppercase py-4 transition-colors ${tab === "squads" ? "text-volt" : "text-white/50 hover:text-white"}`}
                    >
                        My Squads
                        {tab === "squads" && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-volt" />}
                    </button>
                    <button
                        onClick={() => setTab("players")}
                        data-testid="teams-tab-players"
                        className={`relative font-mono text-xs tracking-[0.24em] uppercase py-4 transition-colors ${tab === "players" ? "text-volt" : "text-white/50 hover:text-white"}`}
                    >
                        Players
                        {tab === "players" && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-volt" />}
                    </button>
                    <button
                        onClick={() => setTab("lft")}
                        data-testid="teams-tab-lft"
                        className={`relative font-mono text-xs tracking-[0.24em] uppercase py-4 transition-colors flex items-center gap-2 ${tab === "lft" ? "text-volt" : "text-white/50 hover:text-white"}`}
                    >
                        Looking for Team
                        {tab === "lft" && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-volt" />}
                    </button>
                </div>
            </section>

            {/* PENDING INVITES */}
            {tab === "squads" && invites.length > 0 && (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-10 pb-2" data-testid="pending-invites-section">
                    <div className="border border-volt/40 bg-volt/[0.04] clip-frame p-5">
                        <div className="overline text-volt mb-4">[ Pending invites · {invites.length} ]</div>
                        <div className="space-y-2">
                            {invites.map(inv => <InviteRow key={inv.id} invite={inv} onDone={load} />)}
                        </div>
                    </div>
                </section>
            )}

            {/* SQUADS TAB */}
            {tab === "squads" && (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 pb-32 grid md:grid-cols-2 gap-8" data-testid="teams-grid">
                    {/* CREATED COLUMN */}
                    <div data-testid="col-created">
                        <div className="flex items-end justify-between mb-6 pb-4 border-b border-white/10">
                            <div>
                                <div className="overline mb-2">[ 01 · Captain ]</div>
                                <h2 className="font-display font-black tracking-tighter text-2xl md:text-3xl">Teams I created.</h2>
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">{captained.length} team{captained.length === 1 ? "" : "s"}</div>
                        </div>
                        {captained.length === 0 ? (
                            <EmptyState label="You haven't started a team yet." cta={<button onClick={() => setShowCreate(true)} className="btn-ghost mt-4" data-testid="empty-create-team">+ Create team</button>} />
                        ) : (
                            <div className="space-y-4">
                                {captained.map(t => (
                                    <TeamCard key={t.id} team={t} isCaptain onChanged={load} onInvite={() => setInviteFor(t)} />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* JOINED COLUMN */}
                    <div data-testid="col-joined">
                        <div className="flex items-end justify-between mb-6 pb-4 border-b border-white/10">
                            <div>
                                <div className="overline mb-2">[ 02 · Member ]</div>
                                <h2 className="font-display font-black tracking-tighter text-2xl md:text-3xl">Teams I joined.</h2>
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">{joined.length} team{joined.length === 1 ? "" : "s"}</div>
                        </div>
                        {joined.length === 0 ? (
                            <EmptyState label="No invites accepted yet." />
                        ) : (
                            <div className="space-y-4">
                                {joined.map(t => (
                                    <TeamCard key={t.id} team={t} isCaptain={false} onChanged={load} />
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* PLAYERS TAB */}
            {tab === "players" && (
                <PlayersDirectory
                    players={allPlayers}
                    loading={playersLoading}
                    captained={captained}
                    currentUserId={user.id}
                    onInvited={load}
                />
            )}

            {/* LOOKING FOR TEAM TAB */}
            {tab === "lft" && (
                <LookingForTeamDirectory
                    players={lftPlayers}
                    loading={lftLoading}
                    captained={captained}
                    currentUser={user}
                    onSelfToggled={loadLft}
                    onInvited={load}
                />
            )}

            {/* MODALS */}
            <AnimatePresence>
                {inviteFor && <InviteModal team={inviteFor} onClose={() => setInviteFor(null)} onDone={load} />}
                {showCreate && <CreateTeamModal onClose={() => setShowCreate(false)} onDone={load} />}
            </AnimatePresence>
        </div>
    );
}

const EmptyState = ({ label, cta }) => (
    <div className="border border-white/10 p-10 text-center bg-[#0A0A0A]">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40">{label}</div>
        {cta}
    </div>
);

const TeamCard = ({ team, isCaptain, onChanged, onInvite }) => {
    const members = team.members_hydrated || [];
    const locked = team.locked;
    const [chatOpen, setChatOpen] = useState(false);
    const logoInputRef = useRef(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const leave = async () => {
        if (!window.confirm(`Leave ${team.name}?`)) return;
        try {
            await api.post(`/teams/${team.id}/leave`);
            toast.success(`Left ${team.name}`);
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not leave team"); }
    };
    const remove = async (memberId, gamerTag) => {
        if (!window.confirm(`Remove ${gamerTag} from ${team.name}?`)) return;
        try {
            await api.post(`/teams/${team.id}/remove/${memberId}`);
            toast.success(`${gamerTag} removed`);
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
    };
    const destroy = async () => {
        if (!window.confirm(`Disband ${team.name}? This deletes the team, invites, and chat.`)) return;
        try {
            await api.delete(`/teams/${team.id}`);
            toast.success(`${team.name} disbanded`);
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete team"); }
    };
    const uploadLogo = async (file) => {
        if (!file) return;
        if (file.size > 200 * 1024) { toast.error("Logo must be under 200KB"); return; }
        setUploadingLogo(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/teams/${team.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Logo updated");
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
        finally { setUploadingLogo(false); }
    };
    return (
        <div className={`border ${locked ? "border-signal/50" : "border-white/10"} clip-frame bg-[#0A0A0A] p-5`} data-testid={`team-card-${team.tag}`}>
            <div className="flex items-start justify-between mb-4 gap-4">
                <div className="flex items-start gap-4 min-w-0">
                    <div className="relative flex-shrink-0">
                        <div className={`w-16 h-16 border ${team.logo_url ? "border-volt/40" : "border-dashed border-white/15"} bg-[#050505] flex items-center justify-center overflow-hidden`} data-testid={`team-logo-${team.tag}`}>
                            {team.logo_url ? (
                                <img src={team.logo_url} alt={`${team.name} logo`} className="w-full h-full object-cover" />
                            ) : (
                                <span className="font-display font-black text-volt/60 text-lg">{team.tag.slice(0, 2)}</span>
                            )}
                        </div>
                        {isCaptain && (
                            <>
                                <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                                    className="hidden" data-testid={`logo-file-input-${team.tag}`} />
                                <button
                                    onClick={() => logoInputRef.current?.click()}
                                    disabled={uploadingLogo}
                                    title={team.logo_url ? "Replace logo" : "Upload logo"}
                                    className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center bg-volt text-void border border-void hover:bg-white transition-colors font-mono text-xs font-bold disabled:opacity-40"
                                    data-testid={`upload-logo-btn-${team.tag}`}
                                >
                                    {uploadingLogo ? "…" : team.logo_url ? "✎" : "+"}
                                </button>
                            </>
                        )}
                    </div>
                    <div className="min-w-0">
                        <div className="overline text-white/40 mb-1.5 flex items-center gap-2 flex-wrap">
                            <span>{GAME_SHORT[team.game] || team.game}</span>
                            {locked && (
                                <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-signal border border-signal/50 px-1.5 py-0.5" data-testid={`locked-badge-${team.tag}`} title={`Locked · ${locked.name}`}>🔒 LOCKED</span>
                            )}
                        </div>
                        <div className="font-display font-extrabold text-2xl leading-tight break-words">{team.name}</div>
                        <div className="font-mono text-[10px] tracking-[0.24em] text-volt mt-1">[{team.tag}]</div>
                    </div>
                </div>
                <div className="text-right flex-shrink-0">
                    <div className="font-display font-black text-2xl text-volt">{team.elo}</div>
                    <div className="overline text-white/40">ELO</div>
                    <div className="mt-1.5 flex justify-end">
                        <RankBadge elo={team.elo} size="xs" testId={`team-rank-${team.tag}`} />
                    </div>
                </div>
            </div>

            {locked && (
                <div className="mb-4 border border-signal/30 bg-signal/[0.06] px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-signal">
                    Roster locked · {locked.name}
                </div>
            )}

            {(team.registrations || []).length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2" data-testid={`team-registrations-${team.tag}`}>
                    {team.registrations.map((r) => (
                        <Link
                            key={r.tournament_slug}
                            to={r.test_bracket ? `/bracket/${r.tournament_slug}` : `/tournaments`}
                            data-testid={`team-registration-${team.tag}-${r.tournament_slug}`}
                            className={`inline-flex items-center gap-2 border px-2.5 py-1 font-mono text-[10px] tracking-[0.22em] uppercase transition-colors ${
                                r.test_bracket ? "border-volt/50 text-volt hover:bg-volt/[0.08]" : "border-white/20 text-white/70 hover:text-white hover:border-white/40"
                            }`}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${r.status === "live" ? "bg-signal live-dot" : r.status === "finished" ? "bg-white/30" : "bg-volt"}`} />
                            <span>{r.tournament_name}</span>
                            <span className="text-white/40">·</span>
                            <span className="text-volt">{r.points} pts</span>
                        </Link>
                    ))}
                </div>
            )}

            <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-3">
                    <div className="overline text-white/40">Roster · {members.length}</div>
                    {isCaptain && (
                        <button onClick={onInvite} disabled={!!locked} className="btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`invite-btn-${team.tag}`}>+ Invite player</button>
                    )}
                </div>
                <div className="space-y-1">
                    {members.map(m => (
                        <div key={m.id} className="flex items-center justify-between py-1.5 border-b border-white/5" data-testid={`roster-member-${m.gamer_tag}`}>
                            <div className="flex items-center gap-3">
                                <Link to={`/player/${encodeURIComponent(m.gamer_tag)}`} className="font-display font-extrabold text-sm hover:text-volt transition-colors">/{m.gamer_tag}</Link>
                                <PlatformIcons user={m} size="xs" testId={`roster-platforms-${m.gamer_tag}`} />
                                {m.role === "captain" && <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-volt border border-volt/40 px-1.5 py-0.5">Captain</span>}
                                {m.id_verification_status === "verified" && <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/50">✓ ID</span>}
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="font-mono text-[10px] tracking-[0.2em] text-white/40 uppercase">{m.state || "—"}</span>
                                <RankBadge elo={m.elo} size="xs" testId={`roster-rank-${m.gamer_tag}`} />
                                {isCaptain && m.role !== "captain" && !locked && (
                                    <button onClick={() => remove(m.id, m.gamer_tag)} className="text-white/30 hover:text-signal text-xs" data-testid={`remove-${m.gamer_tag}`}>×</button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Action row */}
                <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-3">
                    <button onClick={() => setChatOpen(!chatOpen)} className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/60 hover:text-volt transition-colors" data-testid={`chat-toggle-${team.tag}`}>
                        {chatOpen ? "Close chat →" : "Open team chat →"}
                    </button>
                    <div className="flex gap-3">
                        {!isCaptain && !locked && (
                            <button onClick={leave} className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40 hover:text-signal transition-colors" data-testid={`leave-btn-${team.tag}`}>Leave</button>
                        )}
                        {isCaptain && !locked && (
                            <button onClick={destroy} className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40 hover:text-signal transition-colors" data-testid={`delete-btn-${team.tag}`}>Disband</button>
                        )}
                    </div>
                </div>

                {chatOpen && <TeamChat teamId={team.id} teamTag={team.tag} />}
            </div>
        </div>
    );
};

const TeamChat = ({ teamId, teamTag }) => {
    const { user } = useAuth();
    const [msgs, setMsgs] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);

    const load = async () => {
        try { const r = await api.get(`/teams/${teamId}/messages`); setMsgs(r.data); }
        catch {}
    };
    useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [teamId]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

    const send = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;
        setSending(true);
        try {
            await api.post(`/teams/${teamId}/messages`, { content: input.trim() });
            setInput("");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
        finally { setSending(false); }
    };

    return (
        <div className="mt-4 pt-4 border-t border-white/10" data-testid={`chat-panel-${teamTag}`}>
            <div className="overline text-white/40 mb-3">[ Team chat ]</div>
            <div className="border border-white/8 bg-[#050505] h-[240px] overflow-y-auto p-3 space-y-2 mb-3">
                {msgs.length === 0 && (
                    <div className="h-full flex items-center justify-center text-white/30 font-mono text-[10px] tracking-[0.28em] uppercase">No messages yet</div>
                )}
                {msgs.map(m => {
                    const mine = m.user_id === user?.id;
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                            <div className={`max-w-[80%] px-3 py-2 border ${mine ? "border-volt/40 bg-volt/[0.08]" : "border-white/10 bg-[#0A0A0A]"}`}>
                                <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/40 mb-1">
                                    /{m.gamer_tag} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                </div>
                                <div className="text-sm text-white/90 whitespace-pre-wrap break-words">{m.content}</div>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>
            <form onSubmit={send} className="flex gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a message…" data-testid={`chat-input-${teamTag}`}
                    maxLength={2000}
                    className="flex-1 bg-void border border-white/15 focus:border-volt/60 outline-none px-3 py-2 font-body text-sm text-white placeholder-white/25" />
                <button type="submit" disabled={sending || !input.trim()} className="btn-primary !py-2 !px-4 !text-[10px] disabled:opacity-40" data-testid={`chat-send-${teamTag}`}>
                    {sending ? "…" : "Send"}
                </button>
            </form>
        </div>
    );
};

const InviteRow = ({ invite, onDone }) => {
    const [busy, setBusy] = useState(false);
    const respond = async (action) => {
        setBusy(true);
        try {
            await api.post(`/invites/${invite.id}/respond`, { action });
            toast.success(action === "accept" ? `Joined ${invite.team?.name}` : "Invite declined");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 py-2" data-testid={`invite-row-${invite.id}`}>
            <div>
                <div className="font-display font-extrabold text-base leading-tight">{invite.team?.name} <span className="font-mono text-[10px] text-volt tracking-[0.24em]">[{invite.team?.tag}]</span></div>
                <div className="font-mono text-[10px] tracking-[0.22em] text-white/50 uppercase mt-0.5">Invited by /{invite.inviter?.gamer_tag} · {GAME_SHORT[invite.team?.game] || invite.team?.game}</div>
            </div>
            <div className="flex gap-2">
                <button disabled={busy} onClick={() => respond("accept")} className="btn-primary !py-2 !px-3 !text-[10px] disabled:opacity-40" data-testid={`accept-invite-${invite.id}`}>Accept</button>
                <button disabled={busy} onClick={() => respond("decline")} className="btn-ghost !py-2 !px-3 !text-[10px] disabled:opacity-40" data-testid={`decline-invite-${invite.id}`}>Decline</button>
            </div>
        </div>
    );
};

const InviteModal = ({ team, onClose, onDone }) => {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [inviting, setInviting] = useState(null);

    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); return; }
        setSearching(true);
        const id = setTimeout(async () => {
            try {
                const r = await api.get("/users/search", { params: { q: q.trim() } });
                setResults(r.data);
            } catch {} finally { setSearching(false); }
        }, 220);
        return () => clearTimeout(id);
    }, [q]);

    const invite = async (gamer_tag) => {
        setInviting(gamer_tag);
        try {
            await api.post(`/teams/${team.id}/invite`, { gamer_tag });
            toast.success(`Invited /${gamer_tag} to ${team.name}`);
            onDone?.();
            onClose();
        } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); } finally { setInviting(null); }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="invite-modal"
        >
            <motion.div
                initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg border border-white/10 clip-frame bg-[#080808] p-6 md:p-8"
            >
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <div className="overline text-volt mb-1">[ Invite player ]</div>
                        <h3 className="font-display font-black tracking-tighter text-2xl leading-tight">to {team.name}</h3>
                        <div className="font-mono text-[10px] tracking-[0.22em] text-white/40 uppercase mt-1">[{team.tag}] · {GAME_SHORT[team.game] || team.game}</div>
                    </div>
                    <button onClick={onClose} className="text-white/40 hover:text-white text-2xl leading-none" data-testid="close-invite-modal">×</button>
                </div>

                <label className="block">
                    <span className="overline text-white/50">Search by gamer tag</span>
                    <input
                        autoFocus type="text" value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="type at least 2 characters…"
                        data-testid="invite-search-input"
                        className="mt-2 w-full bg-void border border-white/15 focus:border-volt/60 outline-none px-4 py-3 font-mono text-sm text-white placeholder-white/25 transition-colors"
                    />
                </label>

                <div className="mt-5 space-y-2 min-h-[120px] max-h-[280px] overflow-y-auto" data-testid="invite-search-results">
                    {q.trim().length < 2 && (
                        <div className="text-center text-white/30 font-mono text-[10px] tracking-[0.28em] uppercase py-10">Start typing…</div>
                    )}
                    {q.trim().length >= 2 && !searching && results.length === 0 && (
                        <div className="text-center text-white/40 font-mono text-[10px] tracking-[0.28em] uppercase py-10">No players match "{q}"</div>
                    )}
                    {results.map(r => (
                        <div key={r.id} className="flex items-center justify-between border border-white/8 clip-frame-sm px-4 py-3 bg-[#0A0A0A]" data-testid={`invite-result-${r.gamer_tag}`}>
                            <div>
                                <div className="font-display font-extrabold text-base leading-tight flex items-center gap-2">
                                    <span>/{r.gamer_tag}</span>
                                    <PlatformIcons user={r} size="xs" />
                                </div>
                                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40 mt-0.5">{r.name} · {r.state || "—"} · {r.elo} ELO</div>
                            </div>
                            <button disabled={inviting === r.gamer_tag} onClick={() => invite(r.gamer_tag)} className="btn-primary !py-2 !px-3 !text-[10px] disabled:opacity-40" data-testid={`invite-send-${r.gamer_tag}`}>
                                {inviting === r.gamer_tag ? "Sending…" : "Invite →"}
                            </button>
                        </div>
                    ))}
                </div>
            </motion.div>
        </motion.div>
    );
};

const CreateTeamModal = ({ onClose, onDone }) => {
    const [step, setStep] = useState("create"); // "create" | "bracket"
    const [form, setForm] = useState({ name: "", tag: "", game: GAMES[0] });
    const [createdTeam, setCreatedTeam] = useState(null);
    const [bracket, setBracket] = useState({ teammate1_gamer_tag: "", teammate2_gamer_tag: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post("/teams", { name: form.name, tag: form.tag.toUpperCase(), game: form.game });
            toast.success("Team created");
            setCreatedTeam(r.data);
            // Only offer bracket entry for Warzone teams (test bracket is a Warzone trio)
            if ((r.data.game || "").toUpperCase().includes("WARZONE")) {
                setStep("bracket");
            } else {
                onDone?.();
                onClose();
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not create team"); } finally { setBusy(false); }
    };

    const enterBracket = async () => {
        if (!createdTeam) return;
        setBusy(true);
        try {
            await api.post("/tournaments/test-warzone-16/join", {
                team_id: createdTeam.id,
                teammate1_gamer_tag: bracket.teammate1_gamer_tag || null,
                teammate2_gamer_tag: bracket.teammate2_gamer_tag || null,
            });
            toast.success(`${createdTeam.name} entered the Test Bracket ✓`);
            onDone?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bracket entry failed");
        } finally { setBusy(false); }
    };

    const skipBracket = () => { onDone?.(); onClose(); };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="create-team-modal"
        >
            <motion.div
                initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md border border-white/10 clip-frame bg-[#080808] p-6 md:p-8"
            >
                {step === "create" && (
                    <>
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <div className="overline text-volt mb-1">[ Step 01 · Register squad ]</div>
                                <h3 className="font-display font-black tracking-tighter text-2xl">Form your team.</h3>
                            </div>
                            <button onClick={onClose} className="text-white/40 hover:text-white text-2xl leading-none" data-testid="close-create-modal">×</button>
                        </div>
                        <form onSubmit={submit} className="space-y-4">
                            <label className="block">
                                <span className="overline text-white/50">Team name</span>
                                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="create-team-name" placeholder="Void Sentinels"
                                    className="mt-2 w-full bg-void border border-white/15 focus:border-volt/60 outline-none px-4 py-3 font-body text-white placeholder-white/25" />
                            </label>
                            <label className="block">
                                <span className="overline text-white/50">Tag (max 5)</span>
                                <input required maxLength={5} value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value.toUpperCase() })}
                                    data-testid="create-team-tag" placeholder="VOID"
                                    className="mt-2 w-full bg-void border border-white/15 focus:border-volt/60 outline-none px-4 py-3 font-mono uppercase tracking-[0.2em] text-white placeholder-white/25" />
                            </label>
                            <label className="block">
                                <span className="overline text-white/50">Game</span>
                                <select value={form.game} onChange={(e) => setForm({ ...form, game: e.target.value })}
                                    data-testid="create-team-game"
                                    className="mt-2 w-full bg-[#0A0A0A] border border-white/15 focus:border-volt/60 outline-none px-4 py-3 font-body text-white">
                                    {GAMES.map(g => <option key={g}>{g}</option>)}
                                </select>
                            </label>
                            <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-40" data-testid="create-team-submit">
                                {busy ? "Creating…" : "Create Team →"}
                            </button>
                        </form>
                    </>
                )}
                {step === "bracket" && createdTeam && (
                    <div data-testid="bracket-join-step">
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <div className="overline text-volt mb-1">[ Step 02 · Optional ]</div>
                                <h3 className="font-display font-black tracking-tighter text-2xl">Enter the Test Bracket?</h3>
                                <p className="mt-2 text-white/60 text-sm">Drop {createdTeam.name} into the sandbox — WSOW scoring, real ELO on the line.</p>
                            </div>
                            <button onClick={skipBracket} className="text-white/40 hover:text-white text-2xl leading-none" data-testid="close-bracket-step">×</button>
                        </div>
                        <div className="space-y-4">
                            <label className="block">
                                <span className="overline text-white/50">Teammate 1 · Gamer tag</span>
                                <input value={bracket.teammate1_gamer_tag} onChange={(e) => setBracket({ ...bracket, teammate1_gamer_tag: e.target.value })}
                                    data-testid="bracket-teammate1" placeholder="Their in-game tag"
                                    className="mt-2 w-full bg-void border border-white/15 focus:border-volt/60 outline-none px-4 py-3 font-body text-white placeholder-white/25" />
                            </label>
                            <label className="block">
                                <span className="overline text-white/50">Teammate 2 · Gamer tag</span>
                                <input value={bracket.teammate2_gamer_tag} onChange={(e) => setBracket({ ...bracket, teammate2_gamer_tag: e.target.value })}
                                    data-testid="bracket-teammate2" placeholder="Their in-game tag"
                                    className="mt-2 w-full bg-void border border-white/15 focus:border-volt/60 outline-none px-4 py-3 font-body text-white placeholder-white/25" />
                            </label>
                            <div className="grid grid-cols-2 gap-3 pt-2">
                                <button onClick={skipBracket} disabled={busy} className="btn-ghost w-full" data-testid="bracket-skip">
                                    Skip for now
                                </button>
                                <button onClick={enterBracket} disabled={busy} className="btn-primary w-full disabled:opacity-40" data-testid="bracket-enter">
                                    {busy ? "Registering…" : "Enter Test Bracket →"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};


const PlayersDirectory = ({ players, loading, captained, currentUserId, onInvited }) => {
    const [q, setQ] = useState("");
    const [openInviteFor, setOpenInviteFor] = useState(null); // player.id whose dropdown is open

    const filtered = useMemo(() => {
        const list = (players || []).filter(p => p.id !== currentUserId);
        if (!q.trim()) return list;
        const needle = q.trim().toLowerCase();
        return list.filter(p =>
            (p.gamer_tag || "").toLowerCase().includes(needle) ||
            (p.name || "").toLowerCase().includes(needle) ||
            (p.state || "").toLowerCase().includes(needle)
        );
    }, [players, q, currentUserId]);

    // Close popover on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (!e.target.closest?.("[data-invite-popover]")) setOpenInviteFor(null);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const invite = async (teamId, teamName, gamer_tag) => {
        try {
            await api.post(`/teams/${teamId}/invite`, { gamer_tag });
            toast.success(`Invited /${gamer_tag} to ${teamName}`);
            setOpenInviteFor(null);
            onInvited?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Invite failed");
        }
    };

    return (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 pb-32" data-testid="players-directory">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6 pb-4 border-b border-white/10">
                <div>
                    <div className="overline mb-2">[ Roster Pool · All players ]</div>
                    <h2 className="font-display font-black tracking-tighter text-2xl md:text-3xl">Recruit the ladder.</h2>
                    <p className="mt-2 text-white/50 text-sm max-w-lg">
                        Every player on the site — invite anyone straight to a squad you captain. They'll see the invite on their Teams tab.
                    </p>
                </div>
                <div className="flex items-center gap-3 min-w-[260px]">
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search tag, name, state…"
                        data-testid="players-search"
                        className="flex-1 bg-void border border-white/15 focus:border-volt/60 outline-none px-3 py-2.5 font-mono text-sm text-white placeholder-white/30 transition-colors"
                    />
                    <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40 whitespace-nowrap">{filtered.length}</span>
                </div>
            </div>

            {loading ? (
                <div className="border border-white/10 p-16 text-center text-white/40 font-mono text-[11px] tracking-[0.28em] uppercase" data-testid="players-loading">Loading players…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-white/10 p-16 text-center text-white/40 font-mono text-[11px] tracking-[0.28em] uppercase" data-testid="players-empty">
                    {q ? `No players match "${q}"` : "No players yet"}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="players-grid">
                    {filtered.map((p) => (
                        <PlayerDirectoryCard
                            key={p.id}
                            player={p}
                            captained={captained}
                            popoverOpen={openInviteFor === p.id}
                            onOpenPopover={() => setOpenInviteFor(p.id)}
                            onClosePopover={() => setOpenInviteFor(null)}
                            onInvite={(teamId, teamName) => invite(teamId, teamName, p.gamer_tag)}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

const PlayerDirectoryCard = ({ player, captained, popoverOpen, onOpenPopover, onClosePopover, onInvite, extra }) => {
    const canInvite = (captained || []).length > 0;
    const btnRef = useRef(null);
    const [coords, setCoords] = useState(null);

    useEffect(() => {
        if (!popoverOpen || !btnRef.current) { setCoords(null); return; }
        const r = btnRef.current.getBoundingClientRect();
        setCoords({ top: r.bottom + 8, right: window.innerWidth - r.right });
    }, [popoverOpen]);

    return (
        <div className="border border-white/10 clip-frame-sm bg-[#0A0A0A] p-4 card-lift" data-testid={`player-card-${player.gamer_tag}`}>
            <div className="flex items-start justify-between gap-3">
                <Link to={`/player/${encodeURIComponent(player.gamer_tag)}`} className="min-w-0 flex-1 group">
                    <div className="font-display font-extrabold text-lg leading-tight truncate flex items-center gap-2">
                        <span className="text-volt">/</span>
                        <span className="group-hover:text-volt transition-colors">{player.gamer_tag}</span>
                        <PlatformIcons user={player} size="xs" />
                    </div>
                    <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-white/45 truncate">
                        {player.name || "—"} · {player.state || "—"}
                    </div>
                </Link>
                <RankBadge elo={player.elo} size="xs" testId={`player-rank-${player.gamer_tag}`} />
            </div>
            {extra}
            <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45">
                    <span className="text-volt">{player.elo || 0}</span> ELO
                    {player.main_game && (
                        <>
                            <span className="mx-2 text-white/20">·</span>
                            <span>{GAME_SHORT[player.main_game] || player.main_game}</span>
                        </>
                    )}
                </div>
                <div data-invite-popover>
                    <button
                        ref={btnRef}
                        disabled={!canInvite}
                        onClick={() => (popoverOpen ? onClosePopover() : onOpenPopover())}
                        data-testid={`invite-player-${player.gamer_tag}`}
                        title={canInvite ? "Invite to a team" : "Create a team first to invite players"}
                        className="btn-primary !py-1.5 !px-3 !text-[10px] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        Invite →
                    </button>
                </div>
            </div>
            {popoverOpen && canInvite && coords && createPortal(
                <div
                    data-invite-popover
                    data-testid={`invite-team-picker-${player.gamer_tag}`}
                    style={{ position: "fixed", top: coords.top, right: coords.right, zIndex: 200 }}
                    className="min-w-[260px] max-w-[320px] border border-volt/40 bg-[#050505] clip-frame-sm shadow-[0_10px_30px_rgba(0,0,0,0.7)]"
                >
                    <div className="px-3 py-2 border-b border-white/10 overline text-white/50">
                        Invite /{player.gamer_tag} to
                    </div>
                    <div className="max-h-[240px] overflow-y-auto">
                        {captained.map((t) => (
                            <button
                                key={t.id}
                                disabled={t.locked}
                                onClick={() => onInvite(t.id, t.name)}
                                data-testid={`invite-target-${player.gamer_tag}-${t.tag}`}
                                className="w-full text-left px-3 py-2.5 border-b border-white/5 last:border-b-0 hover:bg-white/[0.04] transition-colors flex items-center justify-between disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <span>
                                    <span className="font-display font-extrabold text-sm block leading-tight truncate">{t.name}</span>
                                    <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-volt">[{t.tag}] · {GAME_SHORT[t.game] || t.game}</span>
                                </span>
                                {t.locked && <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-signal ml-2">Locked</span>}
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};


const LookingForTeamDirectory = ({ players, loading, captained, currentUser, onSelfToggled, onInvited }) => {
    const [openInviteFor, setOpenInviteFor] = useState(null);
    const [selfLft, setSelfLft] = useState(Boolean(currentUser?.looking_for_team));
    const [saving, setSaving] = useState(false);

    // Sync with fresh /auth/me on mount
    useEffect(() => {
        setSelfLft(Boolean(currentUser?.looking_for_team));
    }, [currentUser]);

    useEffect(() => {
        const onDoc = (e) => {
            if (!e.target.closest?.("[data-invite-popover]")) setOpenInviteFor(null);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const toggleSelfLft = async (next) => {
        setSaving(true);
        try {
            await api.post("/auth/looking-for-team", { looking_for_team: next });
            setSelfLft(next);
            toast.success(next ? "You're now visible to recruiting captains" : "You're no longer listed as LFT");
            onSelfToggled?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        } finally { setSaving(false); }
    };

    const invite = async (teamId, teamName, gamer_tag) => {
        try {
            await api.post(`/teams/${teamId}/invite`, { gamer_tag });
            toast.success(`Invited /${gamer_tag} to ${teamName}`);
            setOpenInviteFor(null);
            onInvited?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Invite failed");
        }
    };

    const visible = (players || []).filter(p => p.id !== currentUser?.id);

    return (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 pb-32" data-testid="lft-directory">
            {/* SELF TOGGLE */}
            <div
                data-testid="lft-self-toggle-card"
                className={`border clip-frame p-5 md:p-6 mb-8 flex flex-wrap items-center justify-between gap-4 transition-colors ${selfLft ? "border-volt/60 bg-volt/[0.06]" : "border-white/10 bg-[#0A0A0A]"}`}
            >
                <div className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${selfLft ? "bg-volt shadow-[0_0_12px_rgba(204,255,0,0.6)] live-dot" : "bg-white/20"}`} />
                    <div>
                        <div className="overline mb-1">[ Your recruiting flag ]</div>
                        <div className="font-display font-black text-xl md:text-2xl leading-tight">
                            {selfLft ? "You're broadcasting as LFT" : "Not looking for a team"}
                        </div>
                        <div className="mt-1 text-white/50 text-sm">
                            {selfLft
                                ? "Captains can see your handle, Discord, and platforms right here and invite you straight to a squad."
                                : "Flip this on to appear in the Looking for Team pool with your handle and Discord."}
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => toggleSelfLft(!selfLft)}
                    disabled={saving}
                    data-testid="lft-self-toggle-btn"
                    className={selfLft ? "btn-ghost" : "btn-primary"}
                >
                    {saving ? "…" : selfLft ? "Stop looking" : "Mark me as LFT"}
                </button>
            </div>

            <div className="flex items-end justify-between gap-4 mb-6 pb-4 border-b border-white/10">
                <div>
                    <div className="overline mb-2">[ Free agents · Looking for team ]</div>
                    <h2 className="font-display font-black tracking-tighter text-2xl md:text-3xl">Recruit free agents.</h2>
                    <p className="mt-2 text-white/50 text-sm max-w-xl">
                        Every player broadcasting as available. Ping them in Discord or invite them straight to a squad you captain.
                    </p>
                </div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40 whitespace-nowrap">
                    {visible.length} available
                </div>
            </div>

            {loading ? (
                <div className="border border-white/10 p-16 text-center text-white/40 font-mono text-[11px] tracking-[0.28em] uppercase" data-testid="lft-loading">Loading free agents…</div>
            ) : visible.length === 0 ? (
                <div className="border border-white/10 p-16 text-center text-white/40 font-mono text-[11px] tracking-[0.28em] uppercase" data-testid="lft-empty">
                    Nobody is looking right now. Be the first — flip your flag above.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lft-grid">
                    {visible.map((p) => (
                        <PlayerDirectoryCard
                            key={p.id}
                            player={p}
                            captained={captained}
                            popoverOpen={openInviteFor === p.id}
                            onOpenPopover={() => setOpenInviteFor(p.id)}
                            onClosePopover={() => setOpenInviteFor(null)}
                            onInvite={(teamId, teamName) => invite(teamId, teamName, p.gamer_tag)}
                            extra={
                                <div className="mt-3 border border-volt/20 bg-volt/[0.04] px-3 py-2 flex items-center justify-between gap-2" data-testid={`lft-discord-${p.gamer_tag}`}>
                                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50">Discord</span>
                                    <span className="font-mono text-xs text-volt truncate">
                                        {p.discord_tag ? p.discord_tag : <span className="text-white/40">— not linked —</span>}
                                    </span>
                                </div>
                            }
                        />
                    ))}
                </div>
            )}
        </section>
    );
};
